/* eslint-disable camelcase */
import { pushRoute } from '../../../lib/router.js';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Col, Grid, Row } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { getOrgTermsContent } from '../../../content/utils.js';
import useConvertToBase64 from '../../../hooks/useConvertToBase64.js';

import {
  getOrgSlug,
  isBase64Image,
  mapFinalFormErrors,
  momentToDateString,
} from '../../../lib/utils.js';
import StepGeneralForm from '../../../components/portfolio/EditPortfolioForms/StepGeneralForm.jsx';
import {
  retrieveProjects,
  selectReadyProjects,
} from '../../../modules/projects.js';
import {
  retrieveProjectStatuses,
  selectProjectStatusesWithArchived,
  selectRetrieveStatusesState,
} from '../../../modules/projectStatuses.js';
import EmployeeFormContainer from '../../employees/EmployeeFormContainer.jsx';
import {
  createPortfolioDraft,
  deletePortfolio,
  finishUpdatePortfolioDraft,
  retrievePortfolio,
  retrievePortfolios,
  selectParentPortfolios,
  selectPortfolio,
  selectPortfolioDraft,
  selectPortfolioDraftId,
  selectRetrievePortfolioState,
  updatePortfolioDraft,
} from '../../../modules/portfolio.js';
import { navigateToStepDetails, onCreateUpdateCancel } from './utils/index.js';
import PortfolioHeader from '../../../components/headers/PageHeaders/Portfolio/PortfolioHeader.jsx';
import MultistepCombinedHeader from '../../../components/headers/FormHeaders/MultistepCombinedHeader.jsx';
import { retrieveOrgGroups } from '../../../modules/orgGroups.js';
import {
  retrieveProjectOwners,
  selectProjectOwners,
  selectProjectOwnersWithArchived,
} from '../../../modules/projectOwners.js';
import useFormInitialValues from '../../../hooks/useFormInitialValues.js';

const mapErrors = mapFinalFormErrors('Failed to save portfolio');

const mapStateToProps = (state, ownProps) => {
  const editing = !!ownProps.portfolioId;
  return {
    editing,
    draftId: selectPortfolioDraftId(state),
    portfolioId: ownProps.portfolioId,
    portfolio: selectPortfolio(ownProps.portfolioId)(state),
    portfolioDraft: selectPortfolioDraft(selectPortfolioDraftId(state))(state),
    owners: editing
      ? selectProjectOwnersWithArchived(state)
      : selectProjectOwners(state),
    projects: selectReadyProjects(state),
    parentPortfolios: selectParentPortfolios(state),
    projectStatuses: selectProjectStatusesWithArchived(state),
    retrievePortfolioSuccess: editing
      ? selectRetrievePortfolioState(state).success
      : true,
    retrieveStatusesSuccess: selectRetrieveStatusesState(state).success,
  };
};

const mapDispatchToProps = {
  retrievePortfolio,
  retrievePortfolios,
  retrieveProjects,
  retrieveProjectStatuses,
  retrieveOrgGroups,
  retrieveProjectOwners,
  createPortfolioDraft,
  finishUpdatePortfolioDraft,
  updatePortfolioDraft,
  deletePortfolio,
};

function StepGeneralFormContainer(props) {
  const {
    editing,
    draftId,
    portfolio,
    projects,
    parentPortfolios,
    portfolioDraft,
    portfolioId,
    retrieveProjectOwners,
    retrieveProjects,
    retrievePortfolios,
    deletePortfolio,
    retrieveProjectStatuses,
    projectStatuses,
    createPortfolioDraft,
    finishUpdatePortfolioDraft,
    updatePortfolioDraft,
    retrievePortfolio,
    retrieveOrgGroups,
    retrievePortfolioSuccess,
    retrieveStatusesSuccess,
  } = props;

  const editingQuery = {
    objectType: 'portfolio',
    objectId: portfolioId,
    active: !editing,
  };

  const { error, convertToBase64 } = useConvertToBase64();
  const [selectedImage, setSelectedImage] = useState('');
  const [imageSize, setImageSize] = useState('');
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [selectableProjects, setSelectableProjects] = useState([]);

  const [selectedEntities, updateSelectedEntities] = useState({
    owners: [],
    sponsors: [],
    projects: [],
    parentPortfolio: [],
  });

  const resource = useMemo(() => {
    const values = draftId ? portfolioDraft : portfolio;

    return {
      ...values,
      project_status:
        values && values.project_status_id
          ? retrieveStatusesSuccess &&
            projectStatuses.find((s) => s.id === values.project_status_id).name
          : '',
    };
  }, [
    portfolio,
    portfolioDraft,
    draftId,
    retrievePortfolioSuccess,
    retrieveStatusesSuccess,
    projectStatuses,
  ]);
  const initialValues = useFormInitialValues(resource);

  const onUpdateSelectedEntities = (key, val) => {
    updateSelectedEntities({
      ...selectedEntities,
      [key]: val,
    });
  };

  const onHideEmployeeModal = () => setShowEmployeeModal(false);

  const onSelectSponsor = (employee) => {
    const isSelected = selectedEntities.sponsors.some(
      (e) => e.id === employee.id
    );

    if (isSelected) {
      if (
        !window.confirm(
          `Are you sure you want to detach this ${getOrgTermsContent('portfolio')} sponsor?`
        )
      )
        return;
      onUpdateSelectedEntities(
        'sponsors',
        selectedEntities.sponsors.filter((e) => e.id !== employee.id)
      );
    } else {
      onUpdateSelectedEntities('sponsors', [
        ...selectedEntities.sponsors,
        employee,
      ]);
    }
  };

  const onAddEmployeeSubmitSuccess = ({ entities, result }) => {
    onSelectSponsor(entities.employees[result]);
  };

  const clearImageField = () => setSelectedImage('');

  const goNext = () =>
    navigateToStepDetails(editing, portfolioId, getOrgSlug());
  const onCancel = () =>
    onCreateUpdateCancel(draftId, deletePortfolio, getOrgSlug());

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    try {
      const result = await convertToBase64(file);
      setSelectedImage(result);
      setImageSize(result.length);
    } catch (err) {
      console.error(error);
    }
  };

  const onSubmit = (values) => {
    const { finish } = values;
    const draft = {
      ...values,
      start_date: momentToDateString(values.start_date),
      end_date: momentToDateString(values.end_date),
      owner_ids: selectedEntities.owners.map((o) => o.id),
      sponsor_ids: selectedEntities.sponsors.map((s) => s.id),
      project_ids: selectedEntities.projects.map((p) => p.id),
      parent_portfolio_id:
        selectedEntities.parentPortfolio.length > 0
          ? selectedEntities.parentPortfolio[0].id
          : null,
      project_status_id: projectStatuses.find(
        (s) => s.name === values.project_status
      ).id,
      image: selectedImage || null,
    };

    const submitPromise = finish
      ? finishUpdatePortfolioDraft(
          !draftId ? portfolioId : draftId,
          draft,
          true
        )
      : !editing
        ? createPortfolioDraft(portfolioId, draft)
        : updatePortfolioDraft(draftId || portfolioId, draft, false, true);

    return submitPromise
      .then(({ result }) =>
        finish
          ? pushRoute('portfolio/show/info', { portfolioId: result })
          : goNext()
      )
      .catch(mapErrors);
  };

  const validate = (values) => {
    const errors = {};
    if (!values.name) errors.name = "Name can't be blank";
    if (!values.project_status)
      errors.project_status = " Status can't be blank";
    if (selectedImage && imageSize) {
      if (!isBase64Image(selectedImage)) {
        errors.image = 'Allowed formats: .jpeg, .png, .jpg';
      }
    }

    if (selectedImage && imageSize > 10485760) {
      errors.image = 'Max file size: 10Mb';
    }
    return errors;
  };

  useEffect(() => {
    portfolioId && retrievePortfolio(portfolioId);
    retrieveProjectOwners({ objectType: 'portfolio', objectId: portfolioId });
    retrieveProjects();
    retrievePortfolios();
    retrieveProjectStatuses({ archived: true });
    retrieveOrgGroups({ active: true });
  }, []);

  useEffect(() => {
    if (resource) {
      const { owners, sponsors, projects, parent_portfolio_id } = resource;
      const parentPortfolio = parent_portfolio_id
        ? parentPortfolios.find((pp) => pp.id === parent_portfolio_id)
        : null;

      updateSelectedEntities({
        owners: owners || [],
        sponsors: sponsors || [],
        projects: projects || [],
        parentPortfolio: parentPortfolio ? [parentPortfolio] : [],
      });
    }
  }, [resource]);

  useEffect(() => {
    let selectableProjects = [...projects.filter((p) => !p.portfolioId)];
    if (editing) {
      selectableProjects = [
        ...projects.filter((p) => p.portfolioId === portfolioId),
        ...selectableProjects,
      ];
    }

    setSelectableProjects(selectableProjects);
  }, [projects]);

  return (
    <React.Fragment>
      <MultistepCombinedHeader
        editing={editing}
        subject={getOrgTermsContent('portfolio')}
        typeStep="General"
      >
        <PortfolioHeader portfolio={portfolio} editing={editing} />
      </MultistepCombinedHeader>
      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              {retrievePortfolioSuccess && retrieveStatusesSuccess ? (
                <Row>
                  <Col sm={10} smOffset={1}>
                    <Form
                      {...props}
                      projects={selectableProjects}
                      parentPortfolios={parentPortfolios}
                      onSubmit={onSubmit}
                      initialValues={initialValues}
                      validate={validate}
                      onCancel={onCancel}
                      onSelectSponsor={onSelectSponsor}
                      handleImageSelect={handleImageSelect}
                      clearImageField={clearImageField}
                      selectedEntities={selectedEntities}
                      employeeSearchParams={editingQuery}
                      updateSelectedEntities={onUpdateSelectedEntities}
                      onShowEmployeeModal={() => setShowEmployeeModal(true)}
                      component={StepGeneralForm}
                    />
                  </Col>
                </Row>
              ) : null}
            </div>
          </Col>
        </Row>
        <EmployeeFormContainer
          showFormModal={showEmployeeModal}
          onHideFormModal={onHideEmployeeModal}
          onSubmitSuccess={onAddEmployeeSubmitSuccess}
        />
      </Grid>
    </React.Fragment>
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StepGeneralFormContainer);
